/**
 * dentalHistoryStore — shared cache for a patient's dental history.
 *
 * Phase 13. Single source of truth for the data
 * `usePatientToothHistory(patientId)` exposes. Previously every
 * consumer (slab + chart + banner + findings strip + add-procedure
 * picker) mounted its own copy of the hook and fired its own fetch
 * chain (`fetchPatientHistory` + N × `listSittingsForVisit` + N ×
 * `fetchVisitMetadata`). On a typical V2 visit page that was 4–5
 * duplicate chains worth ~165 requests.
 *
 * This store:
 *   - holds the raw server data keyed by `patientId`,
 *   - coalesces concurrent `load(patientId)` calls via a module-level
 *     `inFlight` promise map (so even 4 components racing on the same
 *     tick produce ONE request),
 *   - subscribes once at module load to the realtime events that
 *     actually invalidate dental data, with a 250ms debounce so a
 *     multi-event save (PLAN_CREATED → SITTING_SCHEDULED → …) fires
 *     ONE reload, not N,
 *   - tracks live-consumer counts so the cache evicts 60s after the
 *     last consumer unmounts (bounded memory across patient navigation;
 *     re-mounts within the window reuse the cache),
 *   - preserves the optimistic-insert path (`addOptimistic` → `pending`
 *     array, drained on the next server load via meta_id de-dupe).
 *
 * The companion hook `usePatientToothHistory` reads from this store
 * and runs the same `buildIndices` projection, keeping its public
 * return shape identical so no consumer migration is required.
 */
import { create } from 'zustand';

import { listSittingsForVisit } from '../services/api/patientPlanApi';
import { fetchPatientHistory, fetchToothChartBundle } from '../services/api/visitApi';
import { fetchVisitMetadata } from '../services/api/visitMetadataApi';
import eventBus from '../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';


// ── Tunables ────────────────────────────────────────────────────────

// Visit/metadata window. The bundle's `limit` caps only the VISIT LIST and
// the per-visit notes/diagnoses fan-out — NOT sittings. The backend now
// sources sittings from the patient's plans (window-free), so completed
// procedures on older visits always reach the chart regardless of this cap.
// (Previously this windowed sittings too, which silently dropped older
// completed procedures off the chart after enough newer visits accrued.)
const HISTORY_FETCH_LIMIT = 20;

// Hold a patient's cache for 60s after the last consumer unmounts —
// long enough that re-opening the visit page (or the chart dialog) is
// free, short enough that the cache footprint stays bounded.
const EVICT_AFTER_MS = 60_000;

// Debounce realtime invalidations. A save typically emits multiple
// lifecycle events back-to-back (PLAN_CREATED → SITTING_SCHEDULED →
// VISIT_METADATA_ADDED). Without debounce the store would refetch
// 3× in a 50ms window.
const REALTIME_DEBOUNCE_MS = 250;


// ── Module-level coalescing + lifecycle ─────────────────────────────
// Kept OUTSIDE Zustand state to avoid spurious re-renders on promise
// resolution and timer scheduling.

const inFlight = new Map();         // patientId → Promise<void>
const debounceTimers = new Map();   // patientId → setTimeout handle
const evictTimers = new Map();      // patientId → setTimeout handle
const consumerCounts = new Map();   // patientId → integer

// Monotonic version so a memoised projection in the adapter hook can
// cheaply tell when a patient's raw data changed.
let nextVersion = 0;


// ── Async loader ────────────────────────────────────────────────────

/**
 * Legacy fan-out path. Kept as a fallback for deployments running an
 * older backend that doesn't yet expose `/tooth-chart-bundle`. The
 * primary path (`fetchAll` below) hits the bundle endpoint and only
 * falls through here on a 404. Once every environment has the bundle
 * route, this function can be deleted.
 */
async function fetchAllLegacy(patientId) {
  const history = await fetchPatientHistory(patientId);
  const allVisits = history?.visits || [];
  const visits = [...allVisits]
    .sort((a, b) => (b?.visit_date || '').localeCompare(a?.visit_date || ''))
    .slice(0, HISTORY_FETCH_LIMIT);
  const [perVisitSittings, perVisitMetadata] = await Promise.all([
    Promise.all(visits.map(async (v) => {
      try {
        const r = await listSittingsForVisit(v.visit_id);
        return r?.success ? (r.data?.sittings || []) : [];
      } catch {
        return [];
      }
    })),
    Promise.all(visits.map(async (v) => {
      try {
        const r = await fetchVisitMetadata(v.visit_id);
        if (Array.isArray(r?.entries)) return r.entries;
        if (r?.success && Array.isArray(r?.data?.entries)) return r.data.entries;
        return [];
      } catch {
        return [];
      }
    })),
  ]);
  return {
    visits,
    sittings: perVisitSittings.flat(),
    metaEntries: perVisitMetadata.flat(),
  };
}

async function fetchAll(patientId) {
  // Single-request path (server aggregates visits + sittings + metadata).
  try {
    const bundle = await fetchToothChartBundle(patientId, {
      limit: HISTORY_FETCH_LIMIT,
    });
    const visits = Array.isArray(bundle?.visits) ? bundle.visits : [];
    // Flatten the visit-keyed maps; downstream consumers (buildIndices)
    // already group by visit_id from the row itself.
    const sittings = Object.values(bundle?.sittings_by_visit || {}).flat();
    const metaEntries = Object.values(bundle?.metadata_by_visit || {}).flat();

    // Diagnostic guard: warn loudly when sittings exist but none carry
    // tooth_numbers. The frontend's buildIndices drops every such row
    // and the chart looks blank with no error. The fix is a backend
    // restart so the new SittingSummary schema (with tooth_numbers) is
    // live. If this warning fires after a clean restart, the column
    // genuinely never had teeth tagged — open the visit's sitting and
    // check tooth_numbers on the row.
    if (sittings.length > 0
        && !sittings.some((s) => Array.isArray(s?.tooth_numbers)
            && s.tooth_numbers.length > 0)) {
      // eslint-disable-next-line no-console
      console.warn(
        '[dentalHistoryStore] bundle returned %d sittings but NONE carry tooth_numbers — '
        + 'chart will not render procedure colors. Restart the backend so the new '
        + 'SittingSummary schema is loaded, then hard-refresh this page.',
        sittings.length,
      );
    }

    return { visits, sittings, metaEntries };
  } catch (err) {
    // 404 → older backend without the bundle route. Fall back to legacy
    // fan-out so the chart still works during a rolling deploy. Any other
    // error (auth, 5xx, network) re-throws — the caller handles it.
    if (err?.response?.status === 404 || err?.status === 404) {
      // eslint-disable-next-line no-console
      console.info(
        '[dentalHistoryStore] bundle endpoint missing (404); falling back to legacy fan-out',
      );
      return fetchAllLegacy(patientId);
    }
    throw err;
  }
}


const useDentalHistoryStore = create((set, get) => ({
  /**
   * byPatient[patientId]: {
   *   status:      'idle' | 'loading' | 'ready' | 'stale' | 'error',
   *   visits, sittings, metaEntries,
   *   pending:     [] — optimistic entries awaiting server confirm,
   *   loadedAt:    number (Date.now() at last successful load),
   *   version:     number (monotonic; bumps on every state mutation
   *                that the adapter's projection cares about),
   *   error:       Error | null,
   * }
   */
  byPatient: {},

  /**
   * Increment a consumer count for `patientId` and return an
   * unsubscribe function that decrements + arms the eviction timer.
   * The hook calls this from a `useEffect` cleanup so the cache
   * doesn't churn on re-mounts.
   */
  subscribe: (patientId) => {
    if (!patientId) return () => {};
    consumerCounts.set(patientId, (consumerCounts.get(patientId) || 0) + 1);
    // Cancel any pending eviction — somebody just re-subscribed.
    const pendingEvict = evictTimers.get(patientId);
    if (pendingEvict) {
      clearTimeout(pendingEvict);
      evictTimers.delete(patientId);
    }
    return () => {
      const n = (consumerCounts.get(patientId) || 1) - 1;
      if (n > 0) {
        consumerCounts.set(patientId, n);
        return;
      }
      consumerCounts.delete(patientId);
      const handle = setTimeout(() => {
        evictTimers.delete(patientId);
        // Drop the entry only if no consumer re-subscribed in between.
        if ((consumerCounts.get(patientId) || 0) > 0) return;
        set((s) => {
          if (!s.byPatient[patientId]) return s;
          const next = { ...s.byPatient };
          delete next[patientId];
          return { byPatient: next };
        });
      }, EVICT_AFTER_MS);
      evictTimers.set(patientId, handle);
    };
  },

  /**
   * Fetch the patient's dental history if not already cached.
   *  - status === 'ready'  → no-op (cache hit; consumer just reads)
   *  - in-flight promise   → return that promise (request coalescing)
   *  - else                → kick off a new fetch + register it as in-flight
   */
  load: (patientId) => {
    if (!patientId) return Promise.resolve();
    const existing = get().byPatient[patientId];
    if (existing?.status === 'ready') return Promise.resolve();
    const running = inFlight.get(patientId);
    if (running) return running;

    // Mark loading (preserves any prior `pending` entries — an
    // optimistic insert during a stale window must survive the
    // reload that follows).
    set((s) => {
      const prev = s.byPatient[patientId] || {
        visits: [], sittings: [], metaEntries: [],
        pending: [], pendingSittings: [],
      };
      return {
        byPatient: {
          ...s.byPatient,
          [patientId]: { ...prev, status: 'loading', error: null },
        },
      };
    });

    const promise = (async () => {
      try {
        const { visits, sittings, metaEntries } = await fetchAll(patientId);
        set((s) => {
          const prev = s.byPatient[patientId] || { pending: [], pendingSittings: [] };
          const serverIds = new Set(
            metaEntries.map((e) => e?.meta_id).filter(Boolean),
          );
          // Drain optimistic metadata entries the server now confirms.
          const pending = (prev.pending || []).filter(
            (e) => e.meta_id && !serverIds.has(e.meta_id),
          );
          // H4 — drain optimistic sittings whose sitting_id the server
          // now reports. Status-flip optimism (Start/Complete) keeps
          // the same sitting_id, so the drain rule is identical: once
          // the server row appears, the pending row is no longer needed
          // — its job (instant render) is done.
          const serverSittingIds = new Set(
            sittings.map((s2) => s2?.sitting_id).filter(Boolean),
          );
          const pendingSittings = (prev.pendingSittings || []).filter(
            (e) => e.sitting_id && !serverSittingIds.has(e.sitting_id),
          );
          return {
            byPatient: {
              ...s.byPatient,
              [patientId]: {
                status: 'ready',
                visits, sittings, metaEntries, pending, pendingSittings,
                loadedAt: Date.now(),
                version: ++nextVersion,
                error: null,
              },
            },
          };
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[dentalHistoryStore] load failed:', err);
        set((s) => {
          const prev = s.byPatient[patientId] || {
            visits: [], sittings: [], metaEntries: [],
            pending: [], pendingSittings: [],
          };
          return {
            byPatient: {
              ...s.byPatient,
              [patientId]: { ...prev, status: 'error', error: err },
            },
          };
        });
      } finally {
        inFlight.delete(patientId);
      }
    })();
    inFlight.set(patientId, promise);
    return promise;
  },

  /**
   * Mark the cache for `patientId` as stale so the next `load` call
   * actually refetches. Used by realtime handlers when an event
   * indicates the underlying data has changed.
   */
  invalidate: (patientId) => {
    if (!patientId) return;
    set((s) => {
      const prev = s.byPatient[patientId];
      if (!prev || prev.status === 'idle') return s;
      return {
        byPatient: {
          ...s.byPatient,
          [patientId]: { ...prev, status: 'stale' },
        },
      };
    });
  },

  /**
   * Invalidate + load. Used by `refresh()` in the adapter hook and
   * by realtime handlers (after the debounce window expires).
   */
  refresh: (patientId) => {
    if (!patientId) return Promise.resolve();
    get().invalidate(patientId);
    return get().load(patientId);
  },

  /**
   * Optimistic insert — called by `ToothActionsPanel` after a save
   * succeeds. The entry is appended to the pending buffer immediately;
   * it merges into the chart's projection on the next render via the
   * adapter hook. The next server load drains it via `meta_id` de-dupe.
   */
  addOptimistic: (patientId, entry) => {
    if (!patientId || !entry?.meta_id) return;
    set((s) => {
      const prev = s.byPatient[patientId] || {
        visits: [], sittings: [], metaEntries: [],
        pending: [], pendingSittings: [], status: 'idle',
      };
      return {
        byPatient: {
          ...s.byPatient,
          [patientId]: {
            ...prev,
            pending: [...(prev.pending || []), entry],
            version: ++nextVersion,
          },
        },
      };
    });
  },

  /**
   * H4 — Optimistic sitting insert/update. Called by the row-action
   * toolbar right after a successful API call (executeSitting,
   * completeSitting, cancelSitting). Two shapes are supported:
   *
   *   1. New sitting: `{ sitting_id, plan_id, visit_id, status, ... }`
   *      — appended to pendingSittings; merge layer in
   *      usePatientToothHistory picks it up alongside server data.
   *
   *   2. Status-flip on an existing sitting:
   *      `{ sitting_id, status: 'in_progress' }` — same shape, same
   *      pendingSittings array. The merge layer overlays partial
   *      fields onto the matching server row by sitting_id.
   *
   * In both cases the next server bundle refetch drops the pending row
   * once the server reports the same sitting_id (see `load()` drain).
   */
  addOptimisticSitting: (patientId, sitting) => {
    if (!patientId || !sitting?.sitting_id) return;
    set((s) => {
      const prev = s.byPatient[patientId] || {
        visits: [], sittings: [], metaEntries: [],
        pending: [], pendingSittings: [], status: 'idle',
      };
      // Replace any prior optimistic row for the same sitting_id so
      // back-to-back actions (Start then Complete) don't pile up.
      const filtered = (prev.pendingSittings || []).filter(
        (s2) => s2.sitting_id !== sitting.sitting_id,
      );
      return {
        byPatient: {
          ...s.byPatient,
          [patientId]: {
            ...prev,
            pendingSittings: [...filtered, sitting],
            version: ++nextVersion,
          },
        },
      };
    });
  },
}));


// ── Realtime invalidation ───────────────────────────────────────────
// Subscribed once at module load — no per-hook subscriptions needed.
// Each event refreshes every cached patient (typically 1 in practice;
// occasionally 2 when a doctor flips between two patient tabs).
// Debounced 250ms per patient so a save that emits 3 lifecycle events
// in succession produces ONE reload, not three.

const INVALIDATE_EVENTS = [
  // Sitting / plan lifecycle — anything that creates / completes /
  // cancels a sitting changes the dental history projections.
  REALTIME_EVENTS.SITTING_SCHEDULED,
  REALTIME_EVENTS.SITTING_AWAITING_PAYMENT,
  REALTIME_EVENTS.SITTING_PAYMENT_CLEARED,
  REALTIME_EVENTS.SITTING_HELD,
  REALTIME_EVENTS.SITTING_READY,
  REALTIME_EVENTS.SITTING_EXECUTION_STARTED,
  REALTIME_EVENTS.SITTING_COMPLETED,
  REALTIME_EVENTS.SITTING_PENDING_APPROVAL,
  REALTIME_EVENTS.SITTING_APPROVED,
  REALTIME_EVENTS.SITTING_REJECTED,
  REALTIME_EVENTS.SITTING_REASSIGNED,
  REALTIME_EVENTS.SITTING_CANCELLED,
  REALTIME_EVENTS.PLAN_CREATED,
  REALTIME_EVENTS.PLAN_ITEM_CONSUMED,
  REALTIME_EVENTS.PLAN_COMPLETED,
  REALTIME_EVENTS.PLAN_CANCELLED,
  // Bundled collect clears multiple sittings at once — the chart's plan
  // line must reflect payment_cleared without a manual reopen. (Per-sitting
  // SITTING_PAYMENT_CLEARED events also fire, but this guarantees the chart
  // reconciles off the single deterministic end-of-flow event too.)
  REALTIME_EVENTS.VISIT_BUNDLED_PAYMENT_COLLECTED,
  // Local-only events kept for backwards-compat with existing
  // emitters (QuickSearchAdd, ToothActionsPanel save flows).
  'VISIT_METADATA_ADDED',
  'TOOTH_CHART_REFRESH',
].filter(Boolean);

function handleRealtimeInvalidation() {
  const state = useDentalHistoryStore.getState();
  const patientIds = Object.keys(state.byPatient);
  for (const pid of patientIds) {
    const existing = debounceTimers.get(pid);
    if (existing) clearTimeout(existing);
    const handle = setTimeout(() => {
      debounceTimers.delete(pid);
      useDentalHistoryStore.getState().refresh(pid);
    }, REALTIME_DEBOUNCE_MS);
    debounceTimers.set(pid, handle);
  }
}

// Subscribe at module load. The handlers stay registered for the
// lifetime of the tab — same pattern as `sittingStore`'s realtime
// wiring.
for (const eventName of INVALIDATE_EVENTS) {
  eventBus.on(eventName, handleRealtimeInvalidation);
}


export default useDentalHistoryStore;
